import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiEndpoint } from './config.js';
import WorldMap, { getCountryName } from './WorldMap.js';
import './AnalyticsPlatform.css';

const PLATFORMS = [
  { id: 'phone_app', label: 'Mobile App', icon: 'fa-mobile-alt' },
  { id: 'oncorisk', label: 'OncoRisk', icon: 'fa-dna' },
  { id: 'generisk', label: 'GeneRisk', icon: 'fa-heartbeat' },
  { id: 'varxomics', label: 'VarXOmics', icon: 'fa-microscope' }
];

const POLL_INTERVAL = 10000; // 10 seconds for live data

function AnalyticsPlatform() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [selectedPlatform, setSelectedPlatform] = useState('phone_app');
  const [viewMode, setViewMode] = useState('live');
  const [liveData, setLiveData] = useState({ total: 0, breakdown: [] });
  const [historyData, setHistoryData] = useState([]);
  const [countryDetail, setCountryDetail] = useState(null);
  const [countryRows, setCountryRows] = useState([]);
  const [countryRowsRaw, setCountryRowsRaw] = useState([]);
  const [countryTab, setCountryTab] = useState('users');
  const [dataLoading, setDataLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const pollRef = useRef(null);

  // Reset state when switching platforms
  useEffect(() => {
    setLiveData({ total: 0, breakdown: [] });
    setHistoryData([]);
    setLastUpdated(null);
    setSelectedDate(null);
  }, [selectedPlatform]);

  useEffect(() => {
    const token = sessionStorage.getItem('analyticsToken');
    if (token) setIsLoggedIn(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(getApiEndpoint('login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Login failed');

      sessionStorage.setItem('analyticsToken', data.token);
      setIsLoggedIn(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('analyticsToken');
    setIsLoggedIn(false);
    setLiveData({ total: 0, breakdown: [] });
    setHistoryData([]);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const fetchData = useCallback(async (mode, country = null) => {
    const token = sessionStorage.getItem('analyticsToken');
    if (!token) return;

    setDataLoading(true);
    try {
      const body = { platform: selectedPlatform, mode };
      if (country) body.country = country;

      const response = await fetch(getApiEndpoint('query'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error(data.error || 'Query failed');
      }

      if (mode === 'live') {
        const breakdown = data.breakdown || [];
        // Count unique users by 'who' field, aggregate by country
        const uniqueUsers = new Set();
        const countryUsers = {};
        breakdown.forEach(item => {
          const country = item.country || 'Unknown';
          const user = item.who || item.user || item.id;
          if (user) {
            uniqueUsers.add(user);
            if (!countryUsers[country]) {
              countryUsers[country] = new Set();
            }
            countryUsers[country].add(user);
          } else {
            // Fallback: if no user field, count entries
            if (!countryUsers[country]) {
              countryUsers[country] = new Set();
            }
            countryUsers[country].add(`entry_${countryUsers[country].size}`);
            uniqueUsers.add(`entry_${uniqueUsers.size}`);
          }
        });
        const aggregatedBreakdown = Object.entries(countryUsers).map(([country, users]) => ({
          country,
          count: users.size
        }));
        const uniqueTotal = uniqueUsers.size;
        setLiveData({ total: uniqueTotal, breakdown: aggregatedBreakdown });
        setLastUpdated(new Date());
      } else if (mode === 'live:country') {
        const rows = data.rows || [];
        // Store raw activity data
        setCountryRowsRaw(rows);
        // Aggregate by user to show unique users
        const uniqueUsers = {};
        rows.forEach(row => {
          const key = row.who || row.user || 'unknown';
          if (!uniqueUsers[key] || new Date(row.last_active) > new Date(uniqueUsers[key].last_active)) {
            uniqueUsers[key] = { ...row, activity_count: 1 };
          } else {
            uniqueUsers[key].activity_count = (uniqueUsers[key].activity_count || 1) + 1;
          }
        });
        setCountryRows(Object.values(uniqueUsers));
      } else if (mode === 'history') {
        setHistoryData(data.rows || []);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setDataLoading(false);
    }
  }, [selectedPlatform]);

  // Live polling
  useEffect(() => {
    if (!isLoggedIn) return;

    if (viewMode === 'live') {
      fetchData('live');
      pollRef.current = setInterval(() => fetchData('live'), POLL_INTERVAL);
      return () => clearInterval(pollRef.current);
    } else {
      fetchData('history');
    }
  }, [isLoggedIn, selectedPlatform, viewMode, fetchData]);

  const handleCountryClick = async (country) => {
    setCountryDetail(country);
    await fetchData('live:country', country);
  };

  const closeCountryModal = () => {
    setCountryDetail(null);
    setCountryRows([]);
    setCountryRowsRaw([]);
    setCountryTab('users');
  };

  // Aggregate history by date (avoid duplicates)
  const aggregatedHistory = historyData.reduce((acc, row) => {
    const dateKey = row.date?.split('T')[0] || row.date;
    if (!acc[dateKey]) {
      acc[dateKey] = { date: dateKey, countries: {}, totalActivity: 0, totalUnique: 0 };
    }
    acc[dateKey].countries[row.country] = {
      activity: row.activity_count,
      unique: row.unique_users
    };
    acc[dateKey].totalActivity += row.activity_count || 0;
    acc[dateKey].totalUnique += row.unique_users || 0;
    return acc;
  }, {});

  const historyByDate = Object.values(aggregatedHistory)
    .sort((a, b) => b.date.localeCompare(a.date));

  const chartData = historyByDate.slice(0, 14).reverse();
  const maxChartVal = Math.max(...chartData.map(d => d.totalActivity), 1);

  if (!isLoggedIn) {
    return (
      <div className="AP-app-container">
        <div className="AP-login-wrapper">
          <div className="AP-login-card">
            <div className="AP-login-header">
              <i className="fas fa-chart-line AP-login-icon"></i>
              <h1>Analytics</h1>
              <p>Sign in to continue</p>
            </div>

            <form onSubmit={handleLogin} className="AP-login-form">
              {error && (
                <div className="AP-error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              <div className="AP-form-group">
                <label>Username</label>
                <div className="AP-input-wrapper">
                  <i className="fas fa-user"></i>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="AP-form-group">
                <label>Password</label>
                <div className="AP-input-wrapper">
                  <i className="fas fa-lock"></i>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <button type="submit" className="AP-login-btn" disabled={loading}>
                {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="AP-app-container">
      <header className="AP-header">
        <div className="AP-header-left">
          <h1 className="AP-logo-title">Analytics</h1>
          <div className="AP-platform-tabs">
            {PLATFORMS.map(p => (
              <button
                key={p.id}
                className={`AP-platform-tab ${selectedPlatform === p.id ? 'active' : ''}`}
                onClick={() => setSelectedPlatform(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="AP-header-right">
          <div className="AP-view-toggle">
            <button
              className={`AP-view-btn ${viewMode === 'live' ? 'active' : ''}`}
              onClick={() => setViewMode('live')}
            >
              Live
            </button>
            <button
              className={`AP-view-btn ${viewMode === 'history' ? 'active' : ''}`}
              onClick={() => setViewMode('history')}
            >
              History
            </button>
          </div>
          <button className="AP-icon-btn" onClick={handleLogout} title="Logout">
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </header>

      <main className="AP-content">
        {viewMode === 'live' ? (
          <>
            <div className="AP-live-header">
              <div className="AP-stat-main">
                <span className="AP-stat-number">{liveData.total}</span>
                <span className="AP-stat-label">Active Users</span>
              </div>
              <div className="AP-stat-meta">
                <span className="AP-live-indicator">
                  <span className="AP-pulse-dot"></span>
                  Live
                </span>
                {lastUpdated && (
                  <span className="AP-last-updated">
                    Updated {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
                {dataLoading && <i className="fas fa-sync fa-spin AP-spinner"></i>}
              </div>
            </div>

            <div className="AP-map-region-row">
              <div className="AP-map-section">
                <WorldMap data={liveData.breakdown} onCountryClick={handleCountryClick} />
              </div>

              <div className="AP-breakdown">
                <h3>By Region</h3>
                <div className="AP-breakdown-list">
                  {liveData.breakdown.length === 0 ? (
                    <div className="AP-empty">No active users</div>
                  ) : (
                    liveData.breakdown
                      .sort((a, b) => b.count - a.count)
                      .map((item, idx) => (
                        <div
                          key={idx}
                          className="AP-breakdown-row"
                          onClick={() => handleCountryClick(item.country)}
                        >
                          <span className="AP-breakdown-name">{getCountryName(item.country)}</span>
                          <span className="AP-breakdown-count">{item.count}</span>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="AP-history-header">
              <h2>Activity Overview</h2>
              {selectedDate && (
                <button className="AP-clear-filter" onClick={() => setSelectedDate(null)}>
                  Clear filter <i className="fas fa-times"></i>
                </button>
              )}
              {dataLoading && <i className="fas fa-sync fa-spin AP-spinner"></i>}
            </div>

            <div className="AP-chart-section">
              <div className="AP-chart">
                {chartData.map((day, idx) => {
                  const height = (day.totalActivity / maxChartVal) * 100;
                  const isSelected = selectedDate === day.date;
                  return (
                    <div
                      key={idx}
                      className={`AP-bar-col ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedDate(isSelected ? null : day.date)}
                    >
                      <div className="AP-bar" style={{ height: `${Math.max(height, 2)}%` }}>
                        <span className="AP-bar-value">{day.totalActivity.toLocaleString()}</span>
                      </div>
                      <span className="AP-bar-label">{day.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="AP-history-table-wrap">
              <table className="AP-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Region</th>
                    <th>Activity</th>
                    <th>Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedDate
                    ? historyData.filter(r => (r.date?.split('T')[0] || r.date) === selectedDate)
                    : historyData
                  ).map((row, idx) => (
                    <tr
                      key={idx}
                      className={selectedDate === (row.date?.split('T')[0] || row.date) ? 'highlighted' : ''}
                      onClick={() => setSelectedDate(row.date?.split('T')[0] || row.date)}
                    >
                      <td>{row.date?.split('T')[0] || row.date}</td>
                      <td>{getCountryName(row.country)}</td>
                      <td>{row.activity_count?.toLocaleString()}</td>
                      <td>{row.unique_users?.toLocaleString()}</td>
                    </tr>
                  ))}
                  {historyData.length === 0 && (
                    <tr><td colSpan="4" className="AP-empty">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {countryDetail && (
        <div className="AP-modal-overlay" onClick={closeCountryModal}>
          <div className="AP-modal" onClick={e => e.stopPropagation()}>
            <div className="AP-modal-header">
              <h2>{getCountryName(countryDetail)}</h2>
              <div className="AP-modal-tabs">
                <button
                  className={`AP-modal-tab ${countryTab === 'users' ? 'active' : ''}`}
                  onClick={() => setCountryTab('users')}
                >
                  Users ({countryRows.length})
                </button>
                <button
                  className={`AP-modal-tab ${countryTab === 'activity' ? 'active' : ''}`}
                  onClick={() => setCountryTab('activity')}
                >
                  Activity ({countryRowsRaw.length})
                </button>
              </div>
              <button className="AP-icon-btn" onClick={closeCountryModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="AP-modal-body">
              {countryTab === 'users' ? (
                countryRows.length === 0 ? (
                  <div className="AP-empty">No active users</div>
                ) : (
                  <table className="AP-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>IP</th>
                        <th>Last Active</th>
                        <th>Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {countryRows.map((row, idx) => (
                        <tr key={idx}>
                          <td><code>{row.who}</code></td>
                          <td><code>{row.ip}</code></td>
                          <td>{new Date(row.last_active).toLocaleTimeString()}</td>
                          <td>{row.activity_count || 1}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : (
                countryRowsRaw.length === 0 ? (
                  <div className="AP-empty">No activity</div>
                ) : (
                  <table className="AP-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>IP</th>
                        <th>Time</th>
                        <th>Endpoint</th>
                      </tr>
                    </thead>
                    <tbody>
                      {countryRowsRaw.map((row, idx) => (
                        <tr key={idx}>
                          <td><code>{row.who}</code></td>
                          <td><code>{row.ip}</code></td>
                          <td>{new Date(row.last_active).toLocaleTimeString()}</td>
                          <td className="AP-ellipsis">{row.info}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsPlatform;
